import { Search, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { apiPost } from '../api/client.js';
import { AIBlocks, type AIBlock } from '../components/AIBlocks.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Field, Input, Select } from '../components/Input.js';
import { PageHeader } from '../components/PageHeader.js';
import { Skeleton } from '../components/Skeleton.js';
import { useStores } from '../state/store-context.js';

interface FormValues {
  query: string;
  category: string;
  supplier: string;
  minStrPct: number;
  maxCostUsd: number;
  minProfitCad: number;
  competition: string;
  maxShippingDays: number;
}

export function ResearchPage(): JSX.Element {
  const { selectedStoreId, stores } = useStores();
  const [searchParams] = useSearchParams();
  const prefilledQuery = searchParams.get('q');
  const { register, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      query: prefilledQuery ?? 'wireless earbuds',
      category: 'All',
      supplier: 'All',
      minStrPct: 500,
      maxCostUsd: 20,
      minProfitCad: 8,
      competition: 'Any',
      maxShippingDays: 15,
    },
  });
  const [blocks, setBlocks] = useState<AIBlock[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Honor ?q= arriving after first mount (e.g. via in-app navigation).
  useEffect(() => {
    if (prefilledQuery) setValue('query', prefilledQuery);
  }, [prefilledQuery, setValue]);

  const onSubmit = async (v: FormValues): Promise<void> => {
    setLoading(true);
    setError(null);
    setBlocks(null);
    try {
      const store =
        selectedStoreId === 'all'
          ? 'both stores'
          : (stores.find((s) => s.id === selectedStoreId)?.storeName ?? 'both stores');
      const { query, ...filters } = v;
      const res = await apiPost<{ blocks: AIBlock[] }>('/api/ai/research', {
        query,
        filters,
        store,
      });
      setBlocks(res.blocks);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Research failed. Try again.';
      setError(
        msg.includes('No AI provider configured')
          ? 'AI is not configured yet. Open Admin and pick a provider and paste your API key.'
          : msg,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Product"
        accent="research"
        sub="Find items that meet your margin and shipping rules — AI-scored against your filters."
      />

      <Card title="Filters" description="Tighten the search; we score every result against these rules.">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 14,
              marginBottom: 14,
            }}
          >
            <Field label="Category">
              <Select {...register('category')}>
                {[
                  'All',
                  'Electronics',
                  'Fashion / clothing',
                  'Footwear',
                  'Home & garden',
                  'Toys & hobbies',
                  'Beauty',
                  'Sports',
                ].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </Field>
            <Field label="Supplier">
              <Select {...register('supplier')}>
                {['All', 'AliExpress', 'Amazon', 'Walmart', 'CJ Dropshipping'].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </Field>
            <Field label="Min STR %">
              <Input
                type="number"
                {...register('minStrPct', { valueAsNumber: true })}
                min={100}
                max={5000}
                step={100}
              />
            </Field>
            <Field label="Max cost USD">
              <Input
                type="number"
                {...register('maxCostUsd', { valueAsNumber: true })}
                min={1}
                max={100}
                step={1}
              />
            </Field>
            <Field label="Min profit CAD">
              <Input
                type="number"
                {...register('minProfitCad', { valueAsNumber: true })}
                min={2}
                max={50}
                step={1}
              />
            </Field>
            <Field label="Competition">
              <Select {...register('competition')}>
                {['Any', 'Low only', 'Low & medium', 'High'].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </Field>
            <Field label="Max ship days CA">
              <Input
                type="number"
                {...register('maxShippingDays', { valueAsNumber: true })}
                min={5}
                max={45}
                step={1}
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Input
              {...register('query')}
              placeholder="What product are you researching?"
              style={{ flex: 1 }}
            />
            <Button type="submit" icon={Sparkles} loading={loading}>
              Research with AI
            </Button>
          </div>
        </form>
      </Card>

      <Card
        title="AI Research result"
        description="Sourced from Claude's general market knowledge — confirm pricing and inventory on the supplier site before you list."
      >
        {loading && <Skeleton height={140} />}
        {error && (
          <div
            style={{
              padding: 12,
              borderRadius: 'var(--radius-md)',
              background: 'var(--c-danger-bg)',
              color: 'var(--c-danger)',
              fontSize: 'var(--text-md)',
            }}
          >
            {error}
          </div>
        )}
        {blocks ? (
          <AIBlocks blocks={blocks} />
        ) : (
          !loading &&
          !error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 14,
                borderRadius: 'var(--radius-md)',
                background: 'var(--c-divider)',
                color: 'var(--c-muted)',
                fontSize: 'var(--text-md)',
              }}
            >
              <Search size={18} />
              <span>Tighten the filters and click Research with AI.</span>
            </div>
          )
        )}
      </Card>
    </div>
  );
}
